import {
  PDF_PAGE,
  createPainter,
  drawFooter,
  drawHeaderBand,
  drawSectionHeader,
  estimateTextWidth,
  getPdfPalette,
  wrapText,
} from '../../../shared/pdf/pdfTheme'
import {getDataUrlImageHex, loadTenantLogoImage} from '../../../shared/pdf/pdfTenantLogo'
import {getPhenologyTemporalReference, isCompleteCopilotAiText} from './copilotModel'

const {
  width: PAGE_WIDTH,
  height: PAGE_HEIGHT,
  marginX: MARGIN_X,
  marginBottom: MARGIN_BOTTOM,
} = PDF_PAGE
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_X * 2
const PDF_VISUAL_SELECTOR = '[data-pdf-visual-kind][data-pdf-visual-state]'
const TERMINAL_VISUAL_STATES = new Set(['ready', 'unavailable', 'error'])
const DEFAULT_VISUAL_READINESS_TIMEOUT_MS = 12000
const CHART_MODE_TIMEOUT_MS = 4000
const IMAGE_LOAD_TIMEOUT_MS = 4000
const TENANT_LOGO_TIMEOUT_MS = 5000

const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds))

const withTimeout = (promise, milliseconds, message) =>
  new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => reject(new Error(message)), milliseconds)
    Promise.resolve(promise).then(
      (value) => {
        clearTimeout(timeoutId)
        resolve(value)
      },
      (error) => {
        clearTimeout(timeoutId)
        reject(error)
      }
    )
  })

const emitProgress = (onProgress, stage, details = {}) => {
  if (typeof onProgress === 'function') {
    onProgress({stage, ...details})
  }
}

const finiteNumberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null
  }

  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

const normalizeText = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()

const splitAiInterpretationBlocks = (value) => {
  const blocks = []
  let paragraph = []
  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({kind: 'paragraph', text: paragraph.join(' ')})
      paragraph = []
    }
  }

  String(value || '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .forEach((line) => {
      const text = line.trim()
      if (!text) {
        flushParagraph()
        return
      }
      if (/^[-\u2022]\s+/.test(text)) {
        flushParagraph()
        blocks.push({kind: 'bullet', text: text.replace(/^[-\u2022]\s+/, '')})
        return
      }
      if (
        /^(Evidencia|Evidence|Revisiones sugeridas en el lote|Checks in the field):$/.test(text)
      ) {
        flushParagraph()
        blocks.push({kind: 'heading', text})
        return
      }
      paragraph.push(text)
    })
  flushParagraph()
  return blocks
}

const formatNumber = (value, locale, digits = 1) => {
  const number = finiteNumberOrNull(value)
  if (number === null) {
    return '-'
  }

  return new Intl.NumberFormat(locale || 'es', {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(number)
}

const formatDate = (value, locale = 'es') => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) {
    return '-'
  }

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${match[1]}-${match[2]}-${match[3]}T00:00:00Z`))
}

const formatGeneratedDate = (locale = 'es') =>
  new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date())

const sanitizeFileNameCharacters = (value) => {
  const invalidCharacters = new Set(['<', '>', ':', '"', '/', '\\', '|', '?', '*'])

  return Array.from(String(value || ''))
    .map((character) =>
      invalidCharacters.has(character) || character.charCodeAt(0) < 32 ? '-' : character
    )
    .join('')
}

export const createSpecificCopilotPdfFilename = ({fieldName, landName, locale = 'es'} = {}) => {
  const date = formatGeneratedDate(locale).replace(/[/.]/g, '-')
  const base = ['Análisis con IA', fieldName, landName, date]
    .map(normalizeText)
    .filter(Boolean)
    .map((part) =>
      sanitizeFileNameCharacters(part)
        .replace(/\s+/g, ' ')
        .replace(/[. ]+$/g, '')
        .trim()
    )
    .filter(Boolean)
    .join(' - ')

  return `${base || 'Análisis con IA'}.pdf`
}

const nextPaint = () =>
  new Promise((resolve) => {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => window.requestAnimationFrame(resolve))
      return
    }

    setTimeout(resolve, 0)
  })

const loadImage = (source, timeoutMs = IMAGE_LOAD_TIMEOUT_MS) =>
  new Promise((resolve, reject) => {
    const image = new Image()
    let settled = false
    const timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true
        image.src = ''
        reject(new Error('Timed out while loading a PDF visual'))
      }
    }, timeoutMs)
    const finish = (callback) => (value) => {
      if (settled) {
        return
      }
      settled = true
      clearTimeout(timeoutId)
      callback(value)
    }
    image.onload = finish(() => resolve(image))
    image.onerror = finish((error) => reject(error || new Error('Could not load a PDF visual')))
    image.src = source
  })

const getVisualState = (element) => {
  const explicitState = String(element?.dataset?.pdfVisualState || '')
  if (TERMINAL_VISUAL_STATES.has(explicitState) || explicitState === 'pending') {
    return explicitState
  }

  if (element?.querySelector?.('.specific-copilot-chart__empty')) {
    return 'unavailable'
  }

  if (element?.classList?.contains('specific-copilot-chart')) {
    const svg = element.querySelector('.apexcharts-canvas svg')
    const bounds = svg?.getBoundingClientRect?.()
    if (svg && (bounds?.width || svg.viewBox?.baseVal?.width || svg.getAttribute('width'))) {
      return 'ready'
    }
  }

  if (element?.classList?.contains('specific-copilot-integrated-map')) {
    const canvas = element.querySelector('.specific-copilot-integrated-map__canvas canvas')
    if (canvas?.width && canvas?.height) {
      return 'ready'
    }
  }

  return 'pending'
}

const visualDescriptor = (element, index) => ({
  id: element?.dataset?.pdfVisualId || `visual-${index + 1}`,
  kind: element?.dataset?.pdfVisualKind || 'unknown',
  state: getVisualState(element),
})

export const waitForPdfVisuals = async (
  rootElement,
  {timeoutMs = DEFAULT_VISUAL_READINESS_TIMEOUT_MS} = {}
) => {
  if (!rootElement) {
    return {timedOut: false, visuals: [], pending: []}
  }

  if (typeof document !== 'undefined' && document.fonts?.ready) {
    try {
      await withTimeout(document.fonts.ready, Math.min(timeoutMs, 2500), 'PDF fonts timed out')
    } catch (error) {
      // Font readiness is useful but must never block the export.
    }
  }
  await nextPaint()

  const startedAt = Date.now()
  const inspect = () => {
    const visuals = Array.from(rootElement.querySelectorAll(PDF_VISUAL_SELECTOR)).map(
      visualDescriptor
    )
    return {
      visuals,
      pending: visuals.filter((visual) => !TERMINAL_VISUAL_STATES.has(visual.state)),
    }
  }
  const initial = inspect()
  if (!initial.pending.length) {
    return {...initial, timedOut: false, elapsedMs: Date.now() - startedAt}
  }

  return new Promise((resolve) => {
    let observer = null
    let intervalId = null
    let timeoutId = null
    let settled = false
    const finish = (timedOut) => {
      if (settled) {
        return
      }
      settled = true
      observer?.disconnect()
      if (intervalId) {
        clearInterval(intervalId)
      }
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      const result = inspect()
      resolve({...result, timedOut, elapsedMs: Date.now() - startedAt})
    }
    const check = () => {
      if (!inspect().pending.length) {
        finish(false)
      }
    }

    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(check)
      observer.observe(rootElement, {
        attributes: true,
        attributeFilter: ['data-pdf-visual-state'],
        childList: true,
        subtree: true,
      })
    } else {
      intervalId = setInterval(check, 50)
    }
    timeoutId = setTimeout(() => finish(true), Math.max(0, timeoutMs))
    check()
  })
}

const rgbColorToHex = (value) => {
  const color = String(value || '').trim()
  if (/^#[0-9a-f]{6}$/i.test(color)) {
    return color
  }

  const match = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
  if (!match) {
    return null
  }

  return `#${match
    .slice(1, 4)
    .map((component) => Number(component).toString(16).padStart(2, '0'))
    .join('')}`
}

const firstCssColor = (...values) => {
  for (const value of values) {
    const match = String(value || '').match(/rgba?\([^)]*\)|#[0-9a-f]{3,8}/i)
    if (match) {
      return rgbColorToHex(match[0]) || '#05a1e8'
    }
  }

  return '#05a1e8'
}

const canvasToJpeg = (sourceCanvas) => {
  if (!sourceCanvas?.width || !sourceCanvas?.height) {
    return null
  }

  try {
    const maximumWidth = 1600
    const scale = Math.min(2, maximumWidth / sourceCanvas.width)
    const width = Math.max(1, Math.round(sourceCanvas.width * scale))
    const height = Math.max(1, Math.round(sourceCanvas.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, width, height)
    context.drawImage(sourceCanvas, 0, 0, width, height)

    return {
      dataUrl: canvas.toDataURL('image/jpeg', 0.92),
      width,
      height,
    }
  } catch (error) {
    return null
  }
}

export const serializeSvgForPdf = (svg, sourceWidth, sourceHeight) => {
  const clone = svg.cloneNode(true)
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  clone.setAttribute('width', sourceWidth)
  clone.setAttribute('height', sourceHeight)
  clone.setAttribute('viewBox', `0 0 ${sourceWidth} ${sourceHeight}`)

  // ApexCharts renders multi-series legends as XHTML. Browsers refuse to
  // rasterize an SVG containing that foreignObject, so the legend is drawn
  // separately with native PDF primitives below.
  clone.querySelectorAll('foreignObject').forEach((element) => element.remove())

  const style = document.createElementNS('http://www.w3.org/2000/svg', 'style')
  style.textContent =
    'text { font-family: Poppins, Arial, sans-serif; } .apexcharts-tooltip { display: none; }'
  clone.insertBefore(style, clone.firstChild)

  return new XMLSerializer().serializeToString(clone)
}

const svgToJpeg = async (svg) => {
  if (!svg) {
    return null
  }

  const bounds = svg.getBoundingClientRect()
  const viewBox = svg.viewBox?.baseVal
  const sourceWidth = Math.round(
    bounds.width || viewBox?.width || Number(svg.getAttribute('width'))
  )
  const sourceHeight = Math.round(
    bounds.height || viewBox?.height || Number(svg.getAttribute('height'))
  )

  if (!sourceWidth || !sourceHeight) {
    return null
  }

  const source = serializeSvgForPdf(svg, sourceWidth, sourceHeight)
  const url = URL.createObjectURL(new Blob([source], {type: 'image/svg+xml;charset=utf-8'}))

  try {
    const image = await loadImage(url)
    const scale = Math.min(2, 1600 / sourceWidth)
    const width = Math.max(1, Math.round(sourceWidth * scale))
    const height = Math.max(1, Math.round(sourceHeight * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, width, height)
    context.drawImage(image, 0, 0, width, height)

    return {
      dataUrl: canvas.toDataURL('image/jpeg', 0.92),
      width,
      height,
    }
  } catch (error) {
    return null
  } finally {
    URL.revokeObjectURL(url)
  }
}

const collectDefinitionList = (element) =>
  Array.from(element?.querySelectorAll('dl > div') || [])
    .map((row) => ({
      label: normalizeText(row.querySelector('dt')?.textContent),
      value: normalizeText(row.querySelector('dd')?.textContent),
    }))
    .filter((row) => row.label || row.value)

const collectTable = (wrapper) => {
  const table = wrapper?.querySelector('table')
  if (!table) {
    return null
  }

  const heading = wrapper.previousElementSibling
  const headers = Array.from(table.querySelectorAll('thead th')).map((cell) =>
    normalizeText(cell.textContent)
  )
  const rows = Array.from(table.querySelectorAll('tbody tr')).map((row) =>
    Array.from(row.querySelectorAll('th, td')).map((cell) => {
      const stack = cell.querySelector('.specific-copilot-integrated__cell-stack')
      if (!stack) {
        return normalizeText(cell.textContent)
      }

      return [stack.querySelector('strong')?.textContent, stack.querySelector('small')?.textContent]
        .map(normalizeText)
        .filter(Boolean)
        .join(' · ')
    })
  )

  return {
    title: normalizeText(heading?.querySelector('h5')?.textContent),
    subtitle: normalizeText(heading?.querySelector('p')?.textContent),
    headers,
    rows,
  }
}

const collectChart = async (element) => {
  const title = normalizeText(element.querySelector('h5')?.textContent)
  const activeMode = normalizeText(
    element.querySelector('.specific-copilot-chart__mode button.is-active')?.textContent
  )
  const caption = normalizeText(
    element.querySelector('.specific-copilot-chart__balance')?.textContent
  )
  const empty = normalizeText(element.querySelector('.specific-copilot-chart__empty')?.textContent)
  const visualState = getVisualState(element)
  const image =
    visualState === 'ready'
      ? await svgToJpeg(element.querySelector('.apexcharts-canvas svg'))
      : null
  const legend = Array.from(element.querySelectorAll('.apexcharts-legend-series'))
    .map((series) => {
      const marker = series.querySelector('.apexcharts-legend-marker')
      const markerColor = marker
        ? window.getComputedStyle(marker).backgroundColor || marker.style.color
        : null

      return {
        label: normalizeText(series.querySelector('.apexcharts-legend-text')?.textContent),
        color: rgbColorToHex(markerColor),
      }
    })
    .filter((item) => item.label)

  return {
    ...(image || {}),
    title: [title, activeMode].filter(Boolean).join(' - '),
    caption: caption || empty,
    legend,
    section: element.closest('.specific-copilot-integrated') ? 'integrated' : 'agronomy',
    captureState: image ? 'ready' : visualState === 'ready' ? 'error' : visualState,
  }
}

const waitForChartMode = async (element, targetMode, timeoutMs = CHART_MODE_TIMEOUT_MS) => {
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    await nextPaint()
    await delay(35)

    const activeButton = Array.from(
      element.querySelectorAll('.specific-copilot-chart__mode [data-pdf-chart-mode]')
    ).find((button) => button.dataset.pdfChartMode === targetMode)
    const state = getVisualState(element)
    if (activeButton?.classList.contains('is-active') && TERMINAL_VISUAL_STATES.has(state)) {
      await nextPaint()
      return state
    }
  }

  return 'error'
}

const collectChartVariants = async (element) => {
  const modeButtons = Array.from(
    element.querySelectorAll('.specific-copilot-chart__mode [data-pdf-chart-mode]')
  )

  if (element.dataset.pdfChartVariants !== 'true' || modeButtons.length < 2) {
    return [await collectChart(element)]
  }

  const originalMode = modeButtons.find((button) => button.classList.contains('is-active'))?.dataset
    .pdfChartMode
  const variants = []

  try {
    for (const mode of modeButtons.map((button) => button.dataset.pdfChartMode)) {
      const button = Array.from(
        element.querySelectorAll('.specific-copilot-chart__mode [data-pdf-chart-mode]')
      ).find((item) => item.dataset.pdfChartMode === mode)
      if (!button) {
        variants.push(await collectChart(element))
        continue
      }
      if (!button.classList.contains('is-active')) {
        button.click()
        await waitForChartMode(element, mode)
      }

      variants.push(await collectChart(element))
    }
  } finally {
    const originalButton = Array.from(
      element.querySelectorAll('.specific-copilot-chart__mode [data-pdf-chart-mode]')
    ).find((button) => button.dataset.pdfChartMode === originalMode)
    if (originalButton && !originalButton.classList.contains('is-active')) {
      originalButton.click()
      await waitForChartMode(element, originalMode)
    }
  }

  return variants
}

const collectMap = (rootElement) => {
  const mapElement = rootElement?.querySelector('.specific-copilot-integrated-map')
  if (!mapElement) {
    return null
  }

  const visualState = getVisualState(mapElement)
  const sourceCanvas = mapElement.querySelector('.specific-copilot-integrated-map__canvas canvas')
  const image = visualState === 'ready' ? canvasToJpeg(sourceCanvas) : null
  const activeLayers = Array.from(
    mapElement.querySelectorAll('.specific-copilot-integrated-map__layer.is-active')
  )
    .map((layer) => {
      const labels = Array.from(layer.querySelectorAll(':scope > span'))
      return normalizeText(labels[labels.length - 1]?.textContent)
    })
    .filter(Boolean)
  const legend = Array.from(
    mapElement.querySelectorAll('.specific-copilot-integrated-map__legend-item')
  )
    .map((item) => {
      const name = normalizeText(item.querySelector(':scope > span')?.textContent)
      const detail = normalizeText(item.querySelector('small')?.textContent)
      const marker = item.querySelector('i')
      const markerStyle = marker ? getComputedStyle(marker) : null

      return {
        label: [name, detail].filter(Boolean).join(' · '),
        color: firstCssColor(
          markerStyle?.backgroundImage,
          markerStyle?.borderColor,
          markerStyle?.backgroundColor
        ),
      }
    })
    .filter((item) => item.label)
  const inspectionElement = mapElement.querySelector('.specific-copilot-integrated-map__inspection')

  return {
    ...(image || {}),
    title: normalizeText(mapElement.querySelector('h5')?.textContent),
    subtitle: normalizeText(
      mapElement.querySelector('.specific-copilot-integrated-map__heading p')?.textContent
    ),
    activeLayers,
    legend,
    inspectionTitle: normalizeText(inspectionElement?.querySelector('strong')?.textContent),
    inspectionRows: collectDefinitionList(inspectionElement),
    inspectionEmpty: normalizeText(inspectionElement?.querySelector('p')?.textContent),
    captureState: image ? 'ready' : visualState === 'ready' ? 'error' : visualState,
  }
}

const mapWithConcurrency = async (items, concurrency, mapper) => {
  const results = new Array(items.length)
  let nextIndex = 0
  const workers = Array.from({length: Math.min(concurrency, items.length)}, async () => {
    while (nextIndex < items.length) {
      const index = nextIndex
      nextIndex += 1
      results[index] = await mapper(items[index], index)
    }
  })
  await Promise.all(workers)
  return results
}

export const collectSpecificCopilotPdfSnapshot = async (
  rootElement,
  {onProgress, readinessTimeoutMs = DEFAULT_VISUAL_READINESS_TIMEOUT_MS} = {}
) => {
  emitProgress(onProgress, 'preparing_visuals')
  await nextPaint()
  const readiness = await waitForPdfVisuals(rootElement, {timeoutMs: readinessTimeoutMs})
  if (readiness.timedOut && readiness.pending.length) {
    console.warn('[SpecificCopilotPDF] Visual readiness deadline reached', {
      elapsedMs: readiness.elapsedMs,
      pending: readiness.pending,
    })
  }

  const integrated = rootElement?.querySelector('.specific-copilot-integrated')
  const chartElements = Array.from(rootElement?.querySelectorAll('.specific-copilot-chart') || [])
  let completedCharts = 0
  emitProgress(onProgress, 'capturing_visuals', {completed: 0, total: chartElements.length + 1})
  const chartGroups = await mapWithConcurrency(chartElements, 2, async (chartElement) => {
    try {
      return await collectChartVariants(chartElement)
    } catch (error) {
      console.warn('[SpecificCopilotPDF] Chart capture failed', {
        visualId: chartElement.dataset.pdfVisualId,
        message: error?.message,
      })
      return [await collectChart(chartElement)]
    } finally {
      completedCharts += 1
      emitProgress(onProgress, 'capturing_visuals', {
        completed: completedCharts,
        total: chartElements.length + 1,
      })
    }
  })
  const charts = chartGroups.flat()
  const terrainElement = integrated?.querySelector('.specific-copilot-integrated__terrain')
  const map = collectMap(rootElement)
  emitProgress(onProgress, 'capturing_visuals', {
    completed: chartElements.length + 1,
    total: chartElements.length + 1,
  })

  return {
    charts,
    map,
    captureDiagnostics: {
      readiness,
      unavailableCharts: charts
        .filter((chart) => chart.captureState !== 'ready')
        .map((chart) => ({title: chart.title, state: chart.captureState})),
      mapState: map?.captureState || 'unavailable',
    },
    selection: {
      ambient: normalizeText(integrated?.dataset.pdfAmbient),
      campaign: normalizeText(integrated?.dataset.pdfCampaign),
    },
    terrain: terrainElement
      ? {
          title: normalizeText(terrainElement.querySelector('h5')?.textContent),
          rows: collectDefinitionList(terrainElement),
          note: normalizeText(terrainElement.querySelector('p')?.textContent),
          empty: normalizeText(
            terrainElement.querySelector('.specific-copilot-integrated__empty-block')?.textContent
          ),
        }
      : null,
    tables: Array.from(
      integrated?.querySelectorAll('.specific-copilot-integrated__table-wrap') || []
    )
      .map(collectTable)
      .filter(Boolean),
  }
}

const buildMetricValue = (value, unit, locale, digits = 1) => {
  const number = finiteNumberOrNull(value)
  return number === null ? '-' : `${formatNumber(number, locale, digits)}${unit || ''}`
}

const createPdfSerializer = ({brandLogo, labels, report}) => {
  const palette = getPdfPalette()
  const pages = [[]]
  const imageRegistry = []
  let y = 48

  const commands = () => pages[pages.length - 1]
  const newPage = () => {
    if (commands().length === 0) {
      y = 46
      return false
    }
    pages.push([])
    y = 46
    return true
  }
  const ensureSpace = (height) => {
    const maximumUsableHeight = PAGE_HEIGHT - MARGIN_BOTTOM - 46
    const requestedHeight = Math.min(Math.max(Number(height) || 0, 0), maximumUsableHeight)
    if (y + requestedHeight > PAGE_HEIGHT - MARGIN_BOTTOM) {
      newPage()
      return true
    }

    return false
  }
  const painter = createPainter({
    push: (operation) => commands().push(operation),
    palette,
    pageWidth: PAGE_WIDTH,
    pageHeight: PAGE_HEIGHT,
  })

  const registerImage = (image) => {
    if (!image?.dataUrl || !image.width || !image.height) {
      return null
    }

    const hex = getDataUrlImageHex(image.dataUrl)
    if (!hex) {
      return null
    }

    const name = `Im${imageRegistry.length + 1}`
    imageRegistry.push({...image, hex, name})
    return name
  }

  const paragraph = (value, options = {}) => {
    if (!normalizeText(value)) {
      return
    }

    const x = options.x || MARGIN_X
    const width = options.width || CONTENT_WIDTH
    const size = options.size || 9.5
    const lineHeight = options.lineHeight || Math.ceil(size * 1.45)
    const lines = wrapText(value, width, size, options.bold)

    lines.forEach((line) => {
      ensureSpace(lineHeight + 2)
      painter.text(line, x, y, size, {
        bold: options.bold,
        color: options.color || palette.slate,
      })
      y += lineHeight
    })
    y += options.gapAfter ?? 3
  }

  const sectionTitle = (title, options = {}) => {
    if (options.newPage && commands().length > 0) {
      newPage()
    }
    ensureSpace(42)
    y = drawSectionHeader(painter, y, title)
  }

  const subsection = (title, subtitle = '') => {
    ensureSpace(34)
    painter.text(title, MARGIN_X, y, 10.5, {bold: true, color: palette.ink})
    y += 15
    if (subtitle) {
      paragraph(subtitle, {size: 8.5, color: palette.muted, gapAfter: 6})
    } else {
      y += 5
    }
  }

  const aiInterpretation = (value) => {
    splitAiInterpretationBlocks(value).forEach((block) => {
      if (block.kind === 'heading') {
        paragraph(block.text, {size: 9, bold: true, gapAfter: 2})
        return
      }
      if (block.kind === 'bullet') {
        paragraph(`- ${block.text}`, {
          x: MARGIN_X + 10,
          width: CONTENT_WIDTH - 10,
          size: 9,
          lineHeight: 12,
          gapAfter: 2,
        })
        return
      }
      paragraph(block.text, {size: 9, lineHeight: 12, gapAfter: 6})
    })
  }

  const infoPanel = (values, options = {}) => {
    const items = values.map(normalizeText).filter(Boolean)
    if (!items.length) {
      return
    }

    const width = options.width || CONTENT_WIDTH
    const textSize = options.size || 9
    const lineHeight = Math.ceil(textSize * 1.45)
    const wrapped = items.map((item) => wrapText(item, width - 24, textSize, false))
    const entries = []
    wrapped.forEach((lines, index) => {
      lines.forEach((line) => entries.push({line, height: lineHeight}))
      if (index < wrapped.length - 1) {
        entries.push({line: null, height: 5})
      }
    })

    if (options.keepTogether) {
      const totalHeight = entries.reduce((height, entry) => height + entry.height, 0) + 18
      const maximumUsableHeight = PAGE_HEIGHT - MARGIN_BOTTOM - 46
      if (totalHeight <= maximumUsableHeight) {
        ensureSpace(totalHeight)
      }
    }

    let entryIndex = 0
    while (entryIndex < entries.length) {
      while (entries[entryIndex]?.line === null) {
        entryIndex += 1
      }
      if (entryIndex >= entries.length) {
        break
      }
      if (y + lineHeight + 18 > PAGE_HEIGHT - MARGIN_BOTTOM) {
        newPage()
      }

      const availableContentHeight = PAGE_HEIGHT - MARGIN_BOTTOM - y - 18
      const chunk = []
      let contentHeight = 0
      while (
        entryIndex < entries.length &&
        contentHeight + entries[entryIndex].height <= availableContentHeight
      ) {
        chunk.push(entries[entryIndex])
        contentHeight += entries[entryIndex].height
        entryIndex += 1
      }
      while (chunk[chunk.length - 1]?.line === null) {
        contentHeight -= chunk.pop().height
      }
      if (!chunk.length) {
        newPage()
        continue
      }

      const height = contentHeight + 18
      painter.roundedRect(MARGIN_X, y, width, height, 8, {
        fill: options.fill || palette.primaryFaint,
        stroke: options.stroke || palette.line,
        lineWidth: 0.8,
      })
      painter.roundedRect(MARGIN_X, y + 8, 3, Math.max(height - 16, 2), 1.5, {
        fill: options.accent || palette.primary,
      })
      let cursor = y + 16
      chunk.forEach((entry) => {
        if (entry.line !== null) {
          painter.text(entry.line, MARGIN_X + 13, cursor, textSize, {color: palette.slate})
        }
        cursor += entry.height
      })
      y += height + 10
      if (entryIndex < entries.length) {
        newPage()
      }
    }
  }

  const metricGrid = (metrics, columns = 3, options = {}) => {
    const gap = 8
    const width = (CONTENT_WIDTH - gap * (columns - 1)) / columns
    const rowHeight = options.rowHeight || 52

    for (let index = 0; index < metrics.length; index += columns) {
      ensureSpace(rowHeight + 8)
      const rowY = y
      metrics.slice(index, index + columns).forEach((metric, column) => {
        const x = MARGIN_X + column * (width + gap)
        painter.roundedRect(x, rowY, width, rowHeight, 8, {fill: palette.surfaceAlt})
        painter.roundedRect(x, rowY + 10, 3, rowHeight - 20, 1.5, {fill: palette.primary})
        const labelLines = wrapText(
          String(metric.label || '').toUpperCase(),
          width - 22,
          6.8,
          true
        ).slice(0, 2)
        labelLines.forEach((line, lineIndex) => {
          painter.text(line, x + 13, rowY + 15 + lineIndex * 9, 6.8, {
            bold: true,
            color: palette.muted,
          })
        })
        const value = String(metric.value ?? '-')
        if (options.wrapValues) {
          let valueSize = options.valueSize || 9.5
          let valueLines = wrapText(value, width - 22, valueSize, true)
          while (valueSize > 7.5 && valueLines.length > 2) {
            valueSize -= 0.5
            valueLines = wrapText(value, width - 22, valueSize, true)
          }
          valueLines = valueLines.slice(0, 2)
          const valueTop = valueLines.length > 1 ? rowY + rowHeight - 20 : rowY + rowHeight - 12
          valueLines.forEach((line, lineIndex) => {
            painter.text(line, x + 13, valueTop + lineIndex * 10, valueSize, {
              bold: true,
              color: palette.ink,
            })
          })
        } else {
          let valueSize = 11.5
          while (valueSize > 8 && estimateTextWidth(value, valueSize, true) > width - 22) {
            valueSize -= 0.5
          }
          painter.text(value, x + 13, rowY + rowHeight - 10, valueSize, {
            bold: true,
            color: palette.ink,
            maxWidth: width - 22,
          })
        }
      })
      y += rowHeight + 8
    }
    y += 4
  }

  const drawProgress = (label, value, percent) => {
    ensureSpace(72)
    painter.roundedRect(MARGIN_X, y, CONTENT_WIDTH, 62, 9, {
      fill: '#fff9e8',
      stroke: '#f1cf73',
      lineWidth: 0.8,
    })
    painter.text(label.toUpperCase(), MARGIN_X + 14, y + 17, 7, {
      bold: true,
      color: '#9a6a00',
    })
    painter.text(value, MARGIN_X + 14, y + 34, 13, {bold: true, color: palette.ink})
    painter.roundedRect(MARGIN_X + 14, y + 45, CONTENT_WIDTH - 28, 5, 2.5, {
      fill: '#eee6c8',
    })
    painter.roundedRect(
      MARGIN_X + 14,
      y + 45,
      Math.max(2, ((CONTENT_WIDTH - 28) * Math.min(Math.max(Number(percent) || 0, 0), 100)) / 100),
      5,
      2.5,
      {fill: '#d59b00'}
    )
    y += 74
  }

  const tableColumnWidths = (count) => {
    if (count === 6) {
      return [86, 106, 61, 69, 86, 107]
    }
    if (count === 5) {
      return [82, 92, 111, 105, 125]
    }
    if (count === 4) {
      return [135, 105, 125, 150]
    }
    if (count === 2) {
      return [225, 290]
    }

    return Array.from({length: count}, () => CONTENT_WIDTH / Math.max(count, 1))
  }

  const drawTable = ({headers = [], rows = [], columnWidths = null, textSize = null}) => {
    if (!headers.length) {
      return
    }

    const widths = columnWidths || tableColumnWidths(headers.length)
    const fontSize = textSize || (headers.length >= 6 ? 6.8 : 7.4)
    const lineHeight = fontSize + 2.2

    const drawHeader = () => {
      const lines = headers.map((header, index) =>
        wrapText(header, widths[index] - 8, fontSize, true)
      )
      const height = Math.max(24, Math.max(...lines.map((value) => value.length)) * lineHeight + 9)
      ensureSpace(height)
      painter.fillRect(MARGIN_X, y, CONTENT_WIDTH, height, palette.navy)
      let x = MARGIN_X
      lines.forEach((cellLines, index) => {
        cellLines.forEach((line, lineIndex) => {
          painter.text(line, x + 4, y + 13 + lineIndex * lineHeight, fontSize, {
            bold: true,
            color: palette.onNavy,
          })
        })
        x += widths[index]
      })
      y += height
      return height
    }

    const headerHeight = drawHeader()
    rows.forEach((row, rowIndex) => {
      const cells = headers.map((_, index) => normalizeText(row[index] ?? '-'))
      const lines = cells.map((cell, index) =>
        wrapText(cell, widths[index] - 8, fontSize, index === 0)
      )
      const rowLineCount = Math.max(...lines.map((value) => value.length))
      const height = Math.max(22, rowLineCount * lineHeight + 8)
      const pageBottom = PAGE_HEIGHT - MARGIN_BOTTOM
      const fullPageRowHeight = pageBottom - 46 - headerHeight

      if (height <= fullPageRowHeight && y + height > pageBottom) {
        newPage()
        drawHeader()
      }

      let offset = 0
      while (offset < rowLineCount) {
        if (pageBottom - y < Math.max(22, lineHeight + 8)) {
          newPage()
          drawHeader()
        }
        const count = Math.min(rowLineCount - offset, Math.floor((pageBottom - y - 8) / lineHeight))
        const chunkHeight = Math.max(22, count * lineHeight + 8)
        painter.fillRect(
          MARGIN_X,
          y,
          CONTENT_WIDTH,
          chunkHeight,
          rowIndex % 2 ? palette.surface : palette.surfaceAlt
        )
        let x = MARGIN_X
        lines.forEach((cellLines, index) => {
          cellLines.slice(offset, offset + count).forEach((line, lineIndex) => {
            painter.text(line, x + 4, y + 13 + lineIndex * lineHeight, fontSize, {
              bold: index === 0,
              color: palette.slate,
            })
          })
          x += widths[index]
        })
        painter.hline(MARGIN_X, y + chunkHeight, MARGIN_X + CONTENT_WIDTH, palette.line, 0.5)
        y += chunkHeight
        offset += count
        if (offset < rowLineCount) {
          newPage()
          drawHeader()
        }
      }
    })
    y += 14
  }

  const visualFigure = (visual, options = {}) => {
    const imageName = registerImage(visual)
    if (!imageName) {
      subsection(visual.title || labels.visual)
      infoPanel([visual.caption || labels.unavailable], {fill: palette.surfaceAlt})
      return
    }

    const legend = Array.isArray(visual.legend) ? visual.legend : []
    const legendFontSize = 8
    const legendGap = 16
    const legendRows = []
    let currentLegendRow = []
    let currentLegendWidth = 0
    legend.forEach((item) => {
      const itemWidth = 13 + estimateTextWidth(item.label, legendFontSize) + legendGap
      if (currentLegendRow.length && currentLegendWidth + itemWidth > CONTENT_WIDTH) {
        legendRows.push(currentLegendRow)
        currentLegendRow = []
        currentLegendWidth = 0
      }
      currentLegendRow.push({...item, width: itemWidth})
      currentLegendWidth += itemWidth
    })
    if (currentLegendRow.length) {
      legendRows.push(currentLegendRow)
    }
    const legendHeight = legendRows.length ? legendRows.length * 16 + 2 : 0
    const cardPadding = 10
    const maximumHeight = options.maximumHeight || 250
    const ratio = Math.min(
      (CONTENT_WIDTH - cardPadding * 2) / visual.width,
      maximumHeight / visual.height
    )
    const imageWidth = Math.max(1, Math.round(visual.width * ratio))
    const imageHeight = Math.max(1, Math.round(visual.height * ratio))
    const cardHeight = imageHeight + cardPadding * 2
    const captionHeight = visual.caption
      ? wrapText(visual.caption, CONTENT_WIDTH, 8.5, false).length * 13 + 6
      : 0
    ensureSpace(34 + captionHeight + legendHeight + cardHeight + 12)
    subsection(visual.title || labels.visual)
    if (visual.caption) {
      paragraph(visual.caption, {size: 8.5, color: palette.muted, gapAfter: 6})
    }
    legendRows.forEach((row) => {
      let legendX = MARGIN_X
      row.forEach((item) => {
        painter.roundedRect(legendX, y + 1, 7, 7, 3.5, {
          fill: item.color || palette.primary,
        })
        painter.text(item.label, legendX + 11, y + 9, legendFontSize, {
          color: palette.slate,
        })
        legendX += item.width
      })
      y += 16
    })
    if (legendRows.length) {
      y += 2
    }
    painter.roundedRect(MARGIN_X, y, CONTENT_WIDTH, cardHeight, 10, {
      fill: palette.surface,
      stroke: palette.line,
      lineWidth: 0.8,
    })
    painter.image(
      imageName,
      MARGIN_X + Math.round((CONTENT_WIDTH - imageWidth) / 2),
      y + cardPadding,
      imageWidth,
      imageHeight
    )
    y += cardHeight + 14
  }

  const agronomy = report.analysis?.agronomicAnalysis || {}
  const profile = agronomy.profile || {}
  const phenology = agronomy.phenology || {}
  const climateSummary = agronomy.climate?.summary || {}
  const periodPolicy = agronomy.periodPolicy || {}
  const sowingCalendarProvenance = agronomy.sowingCalendar?.provenance
  const thermalModelProvenance = phenology.provenance || profile.thermalTime?.provenance
  const developmentModifiers = phenology.developmentModifiers || {}
  const scope = report.analysis?.scope || {}
  const context = [scope.fieldName, scope.landName].map(normalizeText).filter(Boolean).join(' - ')
  const logoName = registerImage(brandLogo)
  const drawLogo = logoName
    ? (currentPainter, {x, top, maxWidth, maxHeight}) => {
        const ratio = Math.min(maxWidth / brandLogo.width, maxHeight / brandLogo.height, 1)
        const width = Math.round(brandLogo.width * ratio)
        const height = Math.round(brandLogo.height * ratio)
        currentPainter.image(logoName, x, top + Math.round((maxHeight - height) / 2), width, height)
      }
    : null

  y = drawHeaderBand(painter, {
    drawLogo,
    eyebrow: labels.eyebrow,
    metaLabel: labels.generated,
    metaValue: formatGeneratedDate(report.locale),
    title: [labels.title, context].filter(Boolean).join(' - '),
    wrapTitle: true,
  })

  metricGrid(
    [
      {
        label: labels.crop,
        value: profile.crop ? report.t(`agronomy.crops.${profile.crop}`) : '-',
      },
      {label: labels.material, value: profile.genotype || '-'},
      {label: labels.sowing, value: formatDate(profile.sowingDate, report.locale)},
      {
        label: profile.harvestDate ? labels.harvest : labels.analysisEnd,
        value: formatDate(profile.harvestDate || periodPolicy.analysisEndDate, report.locale),
      },
    ],
    4,
    {rowHeight: 60, wrapValues: true}
  )

  if (report.question && report.answer) {
    sectionTitle(labels.questionAnswer)
    subsection(labels.question)
    paragraph(report.question)
    subsection(labels.answer)
    paragraph(report.answer, {gapAfter: 8})
  }

  sectionTitle(report.t('agronomy.title'))
  paragraph(report.t('agronomy.subtitle'), {color: palette.muted, gapAfter: 8})
  const methodologyLines = [
    report.t('agronomy.thermal_time.result_summary', {
      base: formatNumber(phenology.baseTemperatureC, report.locale, 1),
      optimum: formatNumber(phenology.optimumTemperatureC, report.locale, 1),
      maximum: formatNumber(phenology.maximumTemperatureC, report.locale, 1),
      source: report.t(
        `agronomy.thermal_time.sources.${phenology.thermalTimeSource || 'suggested'}`
      ),
    }),
  ]
  if (sowingCalendarProvenance?.modelVersion) {
    methodologyLines.push(
      report.t('agronomy.sowing_calendar.methodology', {
        version: sowingCalendarProvenance.modelVersion,
        parameters: sowingCalendarProvenance.parameterSetVersion,
      })
    )
  }
  if (thermalModelProvenance?.modelVersion) {
    methodologyLines.push(
      report.t('agronomy.thermal_time.methodology', {
        version: thermalModelProvenance.modelVersion,
        parameters: thermalModelProvenance.parameterSetVersion,
      }),
      report.t(
        `agronomy.thermal_time.scope_${
          phenology.thermalTimeSource === 'custom' ? 'custom' : 'suggested'
        }`
      )
    )
  }
  if (developmentModifiers.photoperiod?.enabled) {
    methodologyLines.push(
      developmentModifiers.photoperiod.appliedDays > 0
        ? report.t('agronomy.thermal_time.photoperiod.result', {
            days: developmentModifiers.photoperiod.appliedDays,
            factor: formatNumber(developmentModifiers.photoperiod.meanFactor, report.locale, 2),
          })
        : report.t('agronomy.thermal_time.photoperiod.not_applied')
    )
  }
  if (developmentModifiers.vernalization?.enabled) {
    methodologyLines.push(
      developmentModifiers.vernalization.appliedDays > 0
        ? report.t('agronomy.thermal_time.vernalization.result', {
            completion: formatNumber(
              developmentModifiers.vernalization.completionPercent,
              report.locale,
              0
            ),
            accumulated: formatNumber(
              developmentModifiers.vernalization.accumulatedUnits,
              report.locale,
              1
            ),
            required: formatNumber(
              developmentModifiers.vernalization.requiredUnits,
              report.locale,
              1
            ),
          })
        : report.t('agronomy.thermal_time.vernalization.not_applied')
    )
  }
  infoPanel(methodologyLines, {keepTogether: true})

  const temporalReference = getPhenologyTemporalReference(agronomy)
  drawProgress(
    temporalReference.referenceDate
      ? report.t('agronomy.phenology_title_at', {
          date: formatDate(temporalReference.referenceDate, report.locale),
        })
      : report.t('agronomy.phenology_title'),
    report.t(`agronomy.stages.${phenology.stageId || 'unknown'}`),
    phenology.progressPercent
  )
  paragraph(report.t('agronomy.phenology_notice'), {size: 8.8, color: palette.muted})
  if (temporalReference.closed) {
    paragraph(report.t('agronomy.phenology_closed_notice'), {size: 8.8, color: palette.muted})
  }
  if (!temporalReference.referenceDate) {
    paragraph(report.t('agronomy.phenology_date_unknown'), {size: 8.8, color: palette.muted})
  }
  paragraph(report.t('agronomy.confidence_basis'), {size: 8.8, color: palette.muted})

  const timeline = Array.isArray(phenology.timeline) ? phenology.timeline : []
  if (timeline.length) {
    subsection(report.t('agronomy.timeline.title'), report.t('agronomy.timeline.subtitle'))
    drawTable({
      headers: [labels.stage, labels.date, labels.status, labels.accumulatedGdd],
      columnWidths: [105, 75, 225, 110],
      textSize: 7.1,
      rows: timeline.map((item) => {
        const stateKey =
          item.status === 'reached' ? 'reached' : item.basis || item.status || 'forecast_horizon'
        return [
          report.t(`agronomy.stages.${item.stageId || 'unknown'}`),
          formatDate(item.date, report.locale),
          report.t(`agronomy.timeline.states.${stateKey}`),
          buildMetricValue(item.thresholdGdd, ' GDD', report.locale, 0),
        ]
      }),
    })
    if (timeline.some((item) => item?.basis === 'seasonal_forecast')) {
      paragraph(report.t('agronomy.timeline.seasonal_notice'), {
        size: 8.5,
        color: palette.muted,
      })
    }
  }

  ensureSpace(330)
  subsection(labels.metrics)
  metricGrid([
    {
      label: report.t('agronomy.metrics.gdd'),
      value: buildMetricValue(phenology.accumulatedGdd, ' GDD', report.locale, 0),
    },
    {
      label: report.t('agronomy.metrics.gdd_to_next'),
      value: buildMetricValue(phenology.gddToNextStage, ' GDD', report.locale, 0),
    },
    {
      label: report.t('agronomy.metrics.precipitation'),
      value: buildMetricValue(climateSummary.precipitationMm, ' mm', report.locale, 1),
    },
    {
      label: report.t('agronomy.metrics.reference_et0'),
      value: buildMetricValue(climateSummary.referenceEt0Mm, ' mm', report.locale, 1),
    },
    {
      label: report.t('agronomy.metrics.climatic_balance'),
      value: buildMetricValue(climateSummary.climaticBalanceMm, ' mm', report.locale, 1),
    },
    {
      label: report.t('agronomy.metrics.mean_temperature'),
      value: buildMetricValue(climateSummary.meanTemperatureC, ' °C', report.locale, 1),
    },
    {
      label: report.t('agronomy.metrics.minimum_temperature'),
      value: buildMetricValue(climateSummary.minimumTemperatureC, ' °C', report.locale, 1),
    },
    {
      label: report.t('agronomy.metrics.maximum_temperature'),
      value: buildMetricValue(climateSummary.maximumTemperatureC, ' °C', report.locale, 1),
    },
    {
      label: report.t('agronomy.metrics.hot_days'),
      value: buildMetricValue(climateSummary.hotDays, '', report.locale, 0),
    },
    {
      label: report.t('agronomy.metrics.frost_days'),
      value: buildMetricValue(climateSummary.frostDays, '', report.locale, 0),
    },
    {
      label: report.t('agronomy.metrics.dry_spell'),
      value: buildMetricValue(climateSummary.longestDrySpellDays, ' d', report.locale, 0),
    },
  ])

  const agronomyNotices = []
  if (periodPolicy.cappedAutomatically) {
    agronomyNotices.push(
      report.t('agronomy.analysis_period_capped', {
        date: formatDate(periodPolicy.analysisEndDate, report.locale),
      })
    )
  }
  agronomyNotices.push(
    report.t('agronomy.climate_source_notice', {
      source: report.analysis?.agronomicAnalysis?.climate?.source?.name || 'Open-Meteo',
      completeness: formatNumber(climateSummary.completenessPercent, report.locale, 0),
    }),
    report.t('agronomy.climatic_balance_notice')
  )
  infoPanel(agronomyNotices)

  const agronomyCharts = report.snapshot.charts.filter((chart) => chart.section === 'agronomy')
  if (agronomyCharts.length) {
    sectionTitle(labels.charts)
    agronomyCharts.forEach((chart) => visualFigure(chart, {maximumHeight: 225}))
  }

  const hasIntegratedContent =
    report.snapshot.map ||
    report.snapshot.tables.length ||
    report.snapshot.charts.some((chart) => chart.section === 'integrated')

  if (hasIntegratedContent) {
    sectionTitle(report.t('integrated_analysis.title'), {newPage: true})
    paragraph(report.t('integrated_analysis.subtitle'), {color: palette.muted, gapAfter: 8})
    metricGrid(
      [
        {
          label: report.t('integrated_analysis.ambient_label'),
          value: report.snapshot.selection.ambient || '-',
        },
        {
          label: report.t('integrated_analysis.campaign_label'),
          value: report.snapshot.selection.campaign || '-',
        },
      ],
      2
    )

    if (report.snapshot.map) {
      visualFigure(report.snapshot.map, {maximumHeight: 315})
      if (report.snapshot.map.inspectionRows.length) {
        subsection(report.snapshot.map.inspectionTitle)
        drawTable({
          headers: [labels.data, labels.value],
          rows: report.snapshot.map.inspectionRows.map((row) => [row.label, row.value]),
        })
      }
    }

    report.snapshot.charts
      .filter((chart) => chart.section === 'integrated')
      .forEach((chart) => visualFigure(chart, {maximumHeight: 240}))

    if (report.snapshot.terrain) {
      subsection(report.snapshot.terrain.title)
      if (report.snapshot.terrain.rows.length) {
        drawTable({
          headers: [labels.data, labels.value],
          rows: report.snapshot.terrain.rows.map((row) => [row.label, row.value]),
        })
        paragraph(report.snapshot.terrain.note, {size: 8.5, color: palette.muted})
      } else {
        paragraph(report.snapshot.terrain.empty || labels.unavailable, {color: palette.muted})
      }
    }

    report.snapshot.tables.forEach((table) => {
      subsection(table.title, table.subtitle)
      drawTable(table)
    })
  }

  if (report.interpretation.length || report.aiInterpretation?.status !== 'not_requested') {
    sectionTitle(report.t('interpretation.title'))
    paragraph(report.t('interpretation.subtitle'), {color: palette.muted, gapAfter: 8})
    infoPanel([report.t('interpretation.notice')], {fill: palette.surfaceAlt})
    if (report.interpretation.length) {
      subsection(report.t('interpretation.observed_title'))
      report.interpretation.forEach((item) =>
        paragraph(item.text || report.t(item.key, item.values), {
          size: 9,
          lineHeight: 12,
          gapAfter: 5,
        })
      )
    }

    const rawAiText = report.aiInterpretation?.text
    const aiText = isCompleteCopilotAiText(rawAiText) ? String(rawAiText).trim() : ''
    if (report.aiInterpretation?.status !== 'not_requested') {
      subsection(report.t('interpretation.ai_title'))
      if (report.aiInterpretation?.status === 'available' && aiText) {
        aiInterpretation(aiText)
      } else {
        paragraph(report.t('interpretation.ai_unavailable'), {
          size: 9,
          lineHeight: 12,
          gapAfter: 6,
        })
      }
    }
  }

  for (let index = pages.length - 1; index >= 0; index -= 1) {
    if (pages.length > 1 && pages[index].length === 0) {
      pages.splice(index, 1)
    }
  }

  pages.forEach((pageCommands, index) => {
    const pagePainter = createPainter({
      push: (operation) => pageCommands.push(operation),
      palette,
      pageWidth: PAGE_WIDTH,
      pageHeight: PAGE_HEIGHT,
    })
    drawFooter(pagePainter, {index, total: pages.length})
  })

  const objects = []
  const setObject = (id, body) => {
    objects[id] = body
  }
  const nextObjectId = () => objects.length

  setObject(1, '<< /Type /Catalog /Pages 2 0 R >>')
  setObject(3, '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>')
  setObject(
    4,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>'
  )

  imageRegistry.forEach((image) => {
    const objectId = nextObjectId()
    image.objectId = objectId
    setObject(
      objectId,
      `<< /Type /XObject /Subtype /Image /Width ${image.width} /Height ${
        image.height
      } /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter [/ASCIIHexDecode /DCTDecode] /Length ${
        image.hex.length + 1
      } >>\nstream\n${image.hex}>\nendstream`
    )
  })

  const imageResource = imageRegistry
    .map((image) => `/${image.name} ${image.objectId} 0 R`)
    .join(' ')
  const xObjectResource = imageResource ? `/XObject << ${imageResource} >> ` : ''
  const pageIds = []

  pages.forEach((pageCommands) => {
    const contentStream = pageCommands.join('\n')
    const contentId = nextObjectId()
    setObject(
      contentId,
      `<< /Length ${contentStream.length} >>\nstream\n${contentStream}\nendstream`
    )
    const pageId = nextObjectId()
    setObject(
      pageId,
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> ${xObjectResource}>> /Contents ${contentId} 0 R >>`
    )
    pageIds.push(pageId)
  })

  setObject(
    2,
    `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${
      pageIds.length
    } >>`
  )

  let pdf = '%PDF-1.4\n'
  const offsets = [0]
  for (let id = 1; id < objects.length; id += 1) {
    offsets[id] = pdf.length
    pdf += `${id} 0 obj\n${objects[id]}\nendobj\n`
  }

  const xrefOffset = pdf.length
  pdf += `xref\n0 ${objects.length}\n`
  pdf += '0000000000 65535 f \n'
  for (let id = 1; id < objects.length; id += 1) {
    pdf += `${String(offsets[id]).padStart(10, '0')} 00000 n \n`
  }
  pdf += `trailer\n<< /Size ${objects.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`

  return pdf
}

export const buildSpecificCopilotPdf = ({
  analysis,
  snapshot = {},
  interpretation = [],
  aiInterpretation = null,
  question = '',
  answer = '',
  t,
  locale = 'es',
  brandLogo = null,
}) => {
  const safeSnapshot = {
    charts: Array.isArray(snapshot.charts) ? snapshot.charts : [],
    map: snapshot.map || null,
    selection: snapshot.selection || {},
    terrain: snapshot.terrain || null,
    tables: Array.isArray(snapshot.tables) ? snapshot.tables : [],
  }
  const labels = {
    title: t('title'),
    eyebrow: t('pdf.eyebrow'),
    generated: t('pdf.generated'),
    crop: t('agronomy.crop_label'),
    material:
      analysis?.agronomicAnalysis?.profile?.crop === 'maize'
        ? t('agronomy.hybrid_label')
        : t('agronomy.variety_label'),
    sowing: t('agronomy.sowing_date_label'),
    harvest: t('agronomy.harvest_date_label').replace(/\s*\([^)]*\)\s*$/, ''),
    analysisEnd: t('pdf.analysis_end'),
    questionAnswer: t('pdf.question_answer'),
    question: t('pdf.question'),
    answer: t('pdf.answer'),
    stage: t('pdf.stage'),
    date: t('pdf.date'),
    status: t('pdf.status'),
    accumulatedGdd: t('agronomy.metrics.gdd'),
    metrics: t('pdf.metrics'),
    charts: t('pdf.charts'),
    visibleLayers: t('pdf.visible_layers'),
    data: t('pdf.data'),
    value: t('pdf.value'),
    visual: t('pdf.visual'),
    unavailable: t('pdf.unavailable'),
  }

  return createPdfSerializer({
    brandLogo,
    labels,
    report: {
      analysis,
      snapshot: safeSnapshot,
      interpretation: Array.isArray(interpretation) ? interpretation : [],
      aiInterpretation,
      question: normalizeText(question),
      answer: normalizeText(answer),
      t,
      locale,
    },
  })
}

export const triggerSpecificCopilotPdfDownload = ({blob, filename}) => {
  if (!blob || !filename) {
    throw new Error('Copilot PDF download requires a blob and filename')
  }

  if (typeof navigator !== 'undefined' && typeof navigator.msSaveOrOpenBlob === 'function') {
    navigator.msSaveOrOpenBlob(blob, filename)
    return
  }

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  window.setTimeout(() => {
    link.remove()
    URL.revokeObjectURL(url)
  }, 60000)
}

export const downloadSpecificCopilotPdf = async ({
  analysis,
  rootElement,
  interpretation,
  question,
  answer,
  t,
  locale,
  onProgress,
}) => {
  if (!analysis || !rootElement) {
    throw new Error('Copilot PDF requires an analysis and its rendered modal')
  }

  const startedAt = Date.now()
  const snapshot = await collectSpecificCopilotPdfSnapshot(rootElement, {onProgress})
  emitProgress(onProgress, 'building')
  const palette = getPdfPalette()
  let brandLogo = null
  try {
    brandLogo = await withTimeout(
      loadTenantLogoImage(palette.navy),
      TENANT_LOGO_TIMEOUT_MS,
      'Tenant logo loading timed out'
    )
  } catch (error) {
    console.warn('[SpecificCopilotPDF] Continuing without tenant logo', {
      message: error?.message,
    })
  }
  const pdf = buildSpecificCopilotPdf({
    analysis,
    snapshot,
    interpretation,
    aiInterpretation: analysis.aiInterpretation,
    question,
    answer,
    t,
    locale,
    brandLogo,
  })
  const blob = new Blob([pdf], {type: 'application/pdf'})
  const filename = createSpecificCopilotPdfFilename({
    fieldName: analysis.scope?.fieldName,
    landName: analysis.scope?.landName,
    locale,
  })
  emitProgress(onProgress, 'starting_download')

  triggerSpecificCopilotPdfDownload({blob, filename})
  emitProgress(onProgress, 'done', {elapsedMs: Date.now() - startedAt})

  return {blob, filename, diagnostics: snapshot.captureDiagnostics}
}
